/**
 * Social media URL validation and auto-fix utilities.
 *
 * Used by SocialMediaForm to validate links and auto-prefix handles.
 */

const PLATFORM_BASES = {
  instagram: "https://instagram.com/",
  facebook: "https://facebook.com/",
  twitter: "https://x.com/",
  linkedin: "https://linkedin.com/in/",
  website: "https://",
};

/**
 * Regex patterns for validating social media URLs.
 * Each pattern allows the platform's domain with any path after it.
 * All patterns are optional (empty string passes) — validation only runs if value is non-empty.
 */
export const SOCIAL_URL_PATTERNS = {
  instagram: /^https?:\/\/(www\.)?instagram\.com\/.+/i,
  facebook: /^https?:\/\/(www\.)?facebook\.com\/.+/i,
  twitter: /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/.+/i,
  linkedin: /^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/.+/i,
  website: /^https?:\/\/.+\..+/i,
};

const VALIDATION_EXAMPLES = {
  instagram: "https://instagram.com/yourusername",
  facebook: "https://facebook.com/yourpage",
  twitter: "https://x.com/yourusername",
  linkedin: "https://linkedin.com/in/yourprofile",
  website: "https://yourwebsite.com",
};

/**
 * Returns a user-friendly validation error message for a platform.
 */
export const getSocialMediaValidationMessage = (platform) => {
  const example = VALIDATION_EXAMPLES[platform];
  return `Enter a valid URL (e.g., ${example})`;
};

/**
 * Attempts to auto-fix common input mistakes:
 * - Strips leading @ symbol
 * - Prepends platform URL if user typed just a handle/username
 * - Returns the value unchanged if it already looks like a URL
 *
 * @param {string} platform - One of: instagram, facebook, twitter, linkedin, website
 * @param {string} value - The raw user input
 * @returns {string} - The corrected URL, or the original value if no fix was needed
 */
export const autoFixSocialUrl = (platform, value) => {
  if (!value || value.trim() === "") return value;

  const trimmed = value.trim();

  // Already a URL — leave it alone
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // Website field: just prepend https://
  if (platform === "website") {
    // If it has a dot, it's likely a domain
    if (trimmed.includes(".")) {
      return `https://${trimmed}`;
    }
    return trimmed;
  }

  // Social platforms: strip @ and prepend base URL
  const handle = trimmed.replace(/^@/, "");
  const base = PLATFORM_BASES[platform];
  if (base && handle) {
    return `${base}${handle}`;
  }

  return trimmed;
};
